import { API_ENDPOINTS } from '@/utils/constants'
import { api } from '@/lib/api'

export interface UploadUrlRequest {
  fileName: string
  fileType: string
  fileSize: number
  bucketName: string
  metadata?: {
    category?: string
    purpose?: string
  }
  tags?: string[]
}

export interface UploadUrlResponse {
  id: string
  uploadUrl: string
  expiresAt: string
  objectKey: string
  method: string
  headers: Record<string, string>
}

export interface DownloadUrlResponse {
  downloadUrl: string
  expiresAt: string
}

/**
 * Gets a download URL for a file
 */
export async function getDownloadUrl(
  fileId: string,
  expiresIn: number = 3600
): Promise<string | null> {
  if (!fileId) return null

  try {
    const data = await api.get<DownloadUrlResponse>(
      `/storage/download-url/${fileId}?expiresIn=${expiresIn}`
    )
    return data.downloadUrl
  } catch (error) {
    console.error('Error getting download URL:', error)
    return null
  }
}

/**
 * Upload file through backend proxy (solves CORS issues)
 */
export async function uploadProfileImageViaProxy(file: File): Promise<{
  success: boolean
  fileId?: string
  error?: string
}> {
  try {
    // Validate file
    const validation = validateImageFile(file)
    if (!validation.isValid) {
      return {
        success: false,
        error: validation.errors.join(', '),
      }
    }

    // Create FormData for multipart upload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('bucketName', 'images')
    formData.append('metadata', JSON.stringify({
      category: 'profile',
      purpose: 'avatar',
    }))
    formData.append('tags', JSON.stringify(['profile', 'avatar']))

    // Upload through backend proxy
    const response = await fetch(API_ENDPOINTS.STORAGE.UPLOAD, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: formData,
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Upload failed: ${error}`)
    }

    const result = await response.json()
    const fileId = result.data?.id || result.id

    if (!fileId) {
      throw new Error('No file ID returned from upload')
    }

    return {
      success: true,
      fileId,
    }
  } catch (error) {
    console.error('Proxy upload failed:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    }
  }
}

/**
 * Complete profile image upload flow - now uses backend proxy
 */
export async function uploadProfileImage(file: File): Promise<{
  success: boolean
  fileId?: string
  error?: string
}> {
  // Use proxy upload by default to avoid CORS issues
  return uploadProfileImageViaProxy(file)
}

/**
 * Validates an image file
 */
export function validateImageFile(file: File): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  // Check file size (100MB limit)
  const maxSize = 100 * 1024 * 1024
  if (file.size > maxSize) {
    errors.push('File size must be less than 100MB')
  }

  // Check file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
  if (!allowedTypes.includes(file.type)) {
    errors.push('File must be an image (JPEG, PNG, WebP, or GIF)')
  }

  // Check file name
  const invalidChars = /[<>:"/\\|?*]/
  if (invalidChars.test(file.name)) {
    errors.push('File name contains invalid characters')
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Image URL cache for performance
 */
class ImageUrlCache {
  private cache = new Map<string, string>()
  private cacheExpiry = new Map<string, number>()

  async getImageUrl(fileId: string, expiresIn: number = 3600): Promise<string | null> {
    const now = Date.now()
    const cached = this.cache.get(fileId)
    const expiry = this.cacheExpiry.get(fileId)

    // Return cached URL if still valid (with 5-minute buffer)
    if (cached && expiry && expiry - now > 300000) {
      return cached
    }

    // Fetch new URL
    const url = await getDownloadUrl(fileId, expiresIn)
    if (url) {
      this.cache.set(fileId, url)
      this.cacheExpiry.set(fileId, now + expiresIn * 1000)
    }

    return url
  }

  clearExpired(): void {
    const now = Date.now()
    for (const [fileId, expiry] of this.cacheExpiry.entries()) {
      if (expiry <= now) {
        this.cache.delete(fileId)
        this.cacheExpiry.delete(fileId)
      }
    }
  }
}

export const imageUrlCache = new ImageUrlCache()

// Clean expired cache every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => imageUrlCache.clearExpired(), 300000)
}